import { Button, Card, Flex, Form, Input, Table, TableProps, Typography } from 'antd';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import PageTitle from 'components/title/title';
import ComponentHeader from 'components/componentHeader/componentHeader';
import StatusComponent from 'components/status/status';
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';

// models
import { User } from '../models/user';
import FormPassword from 'components/formPassword/formPassword';

const BusinessUsersManagementListPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [resetPasswordForm] = Form.useForm();

	const [users, setUsers] = useState<User[]>([
		{
			id: 1,
			name: 'John Doe',
			status: 'active',
			created_at: '2021-09-01',
			company: 'STC',
		},
	]);
	const [suspendModalOpen, setSuspendModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);

	const columns: TableProps['columns'] = [
		{
			title: t('USERS_MANAGEMENT.USER_NAME'),
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={'green'} />,
		},
		{
			title: t('COMMON.CREATED_AT'),
			dataIndex: 'created_at',
			key: 'created_at',
		},
		{
			title: t('USERS_MANAGEMENT.COMPANY'),
			dataIndex: 'company',
			key: 'company',
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: () => (
				<Flex gap={10} justify="center">
					<ListButton type="edit" text={t('USERS_MANAGEMENT.EDIT_USER')} onClick={() => navigate('update')} />
					<ListButton type="suspend" text={t('USERS_MANAGEMENT.SUSPEND_USER')} onClick={() => setSuspendModalOpen(true)} />
					<ListButton type="reset_password" text={t('USERS_MANAGEMENT.RESET_PASSWORD')} onClick={() => setResetPasswordModalOpen(true)} />
					<ListButton type="delete" text={t('USERS_MANAGEMENT.DELETE_USER')} onClick={() => setDeleteModalOpen(true)} />
				</Flex>
			),
		},
	];

	const validatePassword = (_: any, value: string) => {
		if (!value || resetPasswordForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('COMMON.PASSWORDS_NOT_MATCH')));
	};

	return (
		<>
			<PageTitle name={t('TITLE.USERS_MANAGEMENT')} />

			<Card className="mt-4">
				<ComponentHeader title={t('USERS_MANAGEMENT.ADD_NEW_USER')} subtitle={t('USERS_MANAGEMENT.TO_MANAGE_YOUR_SYSTEM')} />

				<Button type="primary" size="middle" onClick={() => navigate('create')}>
					{t('USERS_MANAGEMENT.ADD_USER')}
				</Button>
			</Card>

			<Table
				className="mt-5"
				columns={columns}
				dataSource={users}
				scroll={{ x: 600 }}
				rowKey="id"
				pagination={{
					pageSize: 5,
					total: users.length,
					showSizeChanger: false,
				}}
				locale={{ emptyText: <NoData /> }}
			/>

			{/* suspend modal */}
			<ConfirmModalComponent
				open={suspendModalOpen}
				confirmText={t('USERS_MANAGEMENT.SUSPEND')}
				confirmColor="danger"
				onConfirm={() => {
					setSuspendModalOpen(false);
				}}
				onDiscard={() => setSuspendModalOpen(false)}
				title={t('USERS_MANAGEMENT.SUSPEND_USER')}
				subTitle={t('USERS_MANAGEMENT.SUSPEND_USER_DESC')}
			/>

			{/* delete modal */}
			<ConfirmModalComponent
				open={deleteModalOpen}
				confirmText={t('COMMON.DELETE')}
				confirmColor="danger"
				onConfirm={() => {
					setDeleteModalOpen(false);
				}}
				onDiscard={() => setDeleteModalOpen(false)}
				title={t('USERS_MANAGEMENT.DELETE_USER')}
				subTitle={t('USERS_MANAGEMENT.DELETE_USER_DESC')}
			/>

			{/* reset password modal */}
			<ConfirmModalComponent
				open={resetPasswordModalOpen}
				onConfirm={() => {
					setResetPasswordModalOpen(false);
				}}
				onDiscard={() => {
					resetPasswordForm.resetFields();
					setResetPasswordModalOpen(false);
				}}
				confirmText={t('COMMON.RESET')}
				title={t('USERS_MANAGEMENT.RESET_USER_PASSWORD')}>
				<Form form={resetPasswordForm}>
					<div>
						<FormPassword
							name="password"
							label={t('COMMON.PASSWORD')}
							rules={[
								{ required: true, message: t('COMMON.PASSWORD_REQUIRED') },
								{ min: 8, message: t('COMMON.PASSWORD_MIN_8') },
							]}
						/>
					</div>

					<div className="mt-4">
						<FormPassword
							name="confirmPassword"
							label={t('COMMON.PASSWORD_CONFIRMATION')}
							rules={[{ required: true, message: t('COMMON.PASSWORD_CONFIRMATION_REQUIRED') }, { validator: validatePassword }]}
						/>
					</div>
				</Form>
			</ConfirmModalComponent>
		</>
	);
};

export default BusinessUsersManagementListPage;
